import { useCallback, useEffect, useState } from 'react';

import { Toast, showErrorToUser } from '@pletly/ui';

import { useEditPasswordForm } from '../../hooks/useEditPasswordFields';
import i18n from '../../i18n';

export const useChangePasswordModal = ({
	visible,
	onClose,
	onChangePassword,
}: {
	visible?: boolean;
	onClose?: () => void;
	onChangePassword: (data: { oldPassword?: string; newPassword: string }) => Promise<unknown>;
}) => {
	const { t } = i18n;

	const formMethods = useEditPasswordForm();
	const [loading, setLoading] = useState(false);
	const { reset, handleSubmit } = formMethods;

	useEffect(() => {
		if (visible) {
			return;
		}

		reset();
	}, [reset, visible]);

	const handlePasswordChange: Parameters<typeof handleSubmit>['0'] = useCallback(
		async ({ password: oldPassword, newPassword }) => {
			setLoading(true);
			try {
				await onChangePassword({ oldPassword, newPassword });

				Toast.show({ type: 'success', text1: t('change-password.password-changed') });
				onClose?.();
				reset();
			} catch (error: unknown) {
				console.error(error);

				if (error instanceof Error && error.message === 'Invalid password') {
					formMethods.setError('password', {
						type: 'manual',
						message: t('change-password.invalid-password'),
					});
				} else {
					showErrorToUser(error as Error);
				}
			} finally {
				setLoading(false);
			}
		},
		[t, reset, formMethods, onChangePassword, onClose],
	);

	const onSubmit = formMethods.handleSubmit(handlePasswordChange);

	return {
		formMethods,
		loading,
		onSubmit,
	};
};
